import React, { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Upload, FileText, X } from 'lucide-react';

interface UploadAreaProps {
  onFileSelect: (file: File) => void;
  selectedFile: File | null;
  onClear: () => void;
}

export const UploadArea: React.FC<UploadAreaProps> = ({ onFileSelect, selectedFile, onClear }) => {
  const { t } = useTranslation();
  const inputRef = useRef<HTMLInputElement>(null);
  const [isDragging, setIsDragging] = useState(false);

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = () => {
    setIsDragging(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      const file = e.dataTransfer.files[0];
      if (file.type === 'application/pdf') {
        onFileSelect(file);
      } else {
        alert('Please upload a PDF file.');
      }
    }
  };

  const handleClick = () => {
    inputRef.current?.click();
  };

  if (selectedFile) {
    return (
      <div className="w-full max-w-xl mx-auto mt-10 animate-in fade-in zoom-in duration-500">
        <div className="bg-white border border-slate-200 rounded-3xl p-8 flex flex-col items-center justify-center relative overflow-hidden group shadow-xl shadow-slate-200/60">
          <div className="absolute inset-0 bg-gradient-to-br from-cyan-50 to-transparent opacity-50" />
          
          <div className="relative z-10 w-20 h-20 bg-white border border-slate-100 rounded-2xl flex items-center justify-center mb-4 shadow-lg shadow-slate-200">
            <div className="w-12 h-12 bg-cyan-100 rounded-xl flex items-center justify-center">
              <FileText className="w-6 h-6 text-cyan-600" />
            </div>
          </div>
          
          <h3 className="relative z-10 text-xl font-bold text-slate-800 mb-1 font-display">{selectedFile.name}</h3>
          <p className="relative z-10 text-slate-500 text-sm mb-6 font-medium">{(selectedFile.size / 1024 / 1024).toFixed(2)} MB</p>
          
          <button 
            onClick={onClear}
            className="absolute top-4 right-4 p-2 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded-full transition-all z-20"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
      </div>
    );
  }

  return (
    <div 
      onClick={handleClick}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
      className={`
        w-full max-w-3xl mx-auto mt-10 rounded-3xl border-2 border-dashed cursor-pointer transition-all duration-300
        flex flex-col items-center justify-center p-12 relative overflow-hidden group
        ${isDragging 
          ? 'border-cyan-500 bg-cyan-50 scale-[1.02]' 
          : 'border-slate-300 bg-white hover:border-cyan-400 hover:bg-slate-50 hover:shadow-xl hover:shadow-slate-200/50'
        }
      `}
    >
      <input 
        type="file" 
        ref={inputRef} 
        onChange={(e) => e.target.files && onFileSelect(e.target.files[0])} 
        className="hidden" 
        accept=".pdf"
      />
      
      {/* Subtle grid pattern for premium feel */}
      <div className="absolute inset-0 opacity-[0.03] bg-[radial-gradient(#000_1px,transparent_1px)] [background-size:16px_16px] pointer-events-none" />

      <div className={`mb-6 p-6 rounded-2xl bg-slate-50 border border-slate-100 shadow-sm transition-transform duration-300 ${isDragging ? 'scale-110 ring-4 ring-cyan-100' : 'group-hover:scale-105'}`}>
        <Upload className={`w-10 h-10 ${isDragging ? 'text-cyan-600' : 'text-slate-400 group-hover:text-cyan-500'}`} />
      </div>
      
      <h3 className="text-2xl font-bold text-slate-800 mb-2 font-display">{t('app.uploadTitle')}</h3>
      <p className="text-slate-500 text-center font-medium whitespace-nowrap">
        {t('app.dragDrop')} <span className="text-cyan-600 underline decoration-cyan-300 decoration-2 underline-offset-2">{t('app.browse')}</span>
      </p>
      <p className="text-xs text-slate-400 mt-4">{t('app.maxSize')}</p>
    </div>
  );
};

